import { ReactNode, useMemo } from 'react';

import { createTheme, CssBaseline, ThemeProvider as DefaultMuiThemeProvider } from '@mui/material';

import mediaQuery from 'css-mediaquery';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';

import { MuiTheme } from './theme/MuiTheme';

interface ThemeProps {
  mode?: 'light' | 'dark';
  children: ReactNode;
  // eslint-disable-next-line @typescript-eslint/ban-types
  overrides?: Record<string, any> & {};
  deviceType?: string;
}

/**
 * Use this to overwrite any MUI styles to match our theme
 * Add type overrides to mui.d.ts
 */
export const MuiThemeProvider = ({ children, deviceType, mode = 'light' }: ThemeProps) => {
  const MuiUseMediaQuery = useMemo(() => {
    const ssrMatchMedia = (query: string) => {
      return {
        matches: mediaQuery.match(query, {
          width: deviceType === 'mobile' ? '0' : `${MuiTheme.breakpoints.values.lg}px`,
        }),
      };
    };

    return deviceType === 'mobile'
      ? {
          defaultProps: {
            ssrMatchMedia,
          },
        }
      : {
          defaultProps: {
            noSsr: true,
          },
        };
  }, [deviceType]);

  const theme = useMemo(
    () =>
      createTheme(MuiTheme, {
        palette: {
          mode,
          ...(mode === 'light'
            ? {
                text: {
                  primary: MuiTheme.palette.grey[900],
                  secondary: MuiTheme.palette.grey[800],
                },
              }
            : {
                background: {
                  default: MuiTheme.palette.neutrals.gray,
                  paper: MuiTheme.palette.neutrals.gray,
                },
                text: {
                  primary: '#fff',
                  secondary: MuiTheme.palette.grey[500],
                },
              }),
        },
        components: {
          MuiUseMediaQuery,
        },
      }),
    [mode]
  );

  return (
    <DefaultMuiThemeProvider theme={theme}>
      <StyledComponentsThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </StyledComponentsThemeProvider>
    </DefaultMuiThemeProvider>
  );
};
